'use client';

import {
  PredictiveTextField,
  TextFieldStatus,
} from '@/lib/mx-asurion-ui-react';
import { ICON_SRC_MAP } from '@/utils/icon-src-conversion';
import { useEffect, useRef, useState } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
} from 'react-places-autocomplete';
import { geocodeStatusMap, searchOptionsByRegion } from './constants';
import { extractAddressComponents, filterDropdownItems } from './helpers';
import { ExtractedAddress, GeocodeDefinedStatus } from './types';

interface AddressSearchProps {
  onAddressSelect: (
    address: ExtractedAddress | undefined,
    isValid: boolean,
  ) => void;
  region?: 'us' | 'uk';
  labelText?: string;
  retryText?: string;
  noResultsText?: string;
  minimumSearchTerms?: number;
  debounce?: number;
  defaultValue?: string;
  disabled?: boolean;
  fieldStatus?: TextFieldStatus;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  onAddressSelect = (
    address: ExtractedAddress | undefined,
    isValid: boolean,
  ) => {},
  region = 'us',
  labelText = 'Your Address',
  retryText = `We&rsquo;re having some problems, please try again later`,
  noResultsText = '',
  minimumSearchTerms = 0,
  debounce = 200,
  disabled = false,
  fieldStatus = 'default',
  defaultValue,
  onBlur,
}) => {
  const [address, setAddress] = useState('');
  const [helpText, setHelpText] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (defaultValue) {
      setAddress(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    const inputElement = document.querySelector('.autocomplete-root input');
    if (inputElement) {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        inputElement as HTMLInputElement;
    }
  }, [inputRef]);

  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      const handleAutofill = () => {
        if (input.matches(':autofill')) {
          setAddress(input.value);
        }
      };
      const timeout = setTimeout(handleAutofill, 100);
      input.click();
      return () => clearTimeout(timeout);
    }
  }, [address, inputRef, setAddress]);

  return (
    <PlacesAutocomplete
      value={address}
      onChange={(address) => setAddress(address)}
      searchOptions={searchOptionsByRegion[region]}
      shouldFetchSuggestions={address.length >= minimumSearchTerms}
      debounce={debounce}
    >
      {({ getInputProps, suggestions }) => {
        const dropdownItems = suggestions
          .filter((suggestion) => filterDropdownItems(suggestion))
          .map((suggestion) => ({
            id: suggestion.placeId,
            header: suggestion.description,
            subHeader: '',
            value: '',
          }));
        return (
          <div className="autocomplete-root fs-mask" ref={inputRef}>
            <PredictiveTextField
              iconSrc={ICON_SRC_MAP.LocationMap}
              label={labelText}
              value={address}
              disabled={disabled}
              includeSubheader={false}
              suggestions={dropdownItems}
              handleChange={(value: string) => {
                getInputProps().onChange({ target: { value } });
                setAddress(value);
                onAddressSelect(undefined, true);
              }}
              noResultsText={noResultsText}
              helperText={helpText}
              onFocus={() => {
                getInputProps().onChange({ target: { value: address } });
              }}
              onSelection={async (suggestion) => {
                try {
                  const [address] = await geocodeByAddress(suggestion.header);
                  setAddress(address.formatted_address);
                  onAddressSelect(
                    extractAddressComponents(address.address_components),
                    true,
                  );
                } catch (status: unknown) {
                  geocodeStatusMap[status as GeocodeDefinedStatus].retryable
                    ? setHelpText(retryText)
                    : null;
                }
              }}
              fieldStatus={fieldStatus}
              onBlur={onBlur}
            />
          </div>
        );
      }}
    </PlacesAutocomplete>
  );
};

export default AddressSearch;
